import {
  Button,
  Flex,
  Heading,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Text,
  useToast,
} from '@chakra-ui/react';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { Alert } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { AuthContext } from '@investigator/shared/providers';
import { useContext, useEffect, useMemo } from 'react';
import { IGetDetectionVariables } from '../../..';
import { ServiceNowEscalationConfiguration } from '../../../../../../shared/generated/graphql';
import { useSearchParams } from 'react-router';
import { i18n } from '../../../../../../shared/localization';
import {
  useEscalateDetections,
  useQueryEscalationConfigurations,
  useQueryEscalationConfigurationsPublic,
} from '../../../../../integrations/queries';
import { DetectionQueryVariables } from '../../../../pages';
import {
  GET_DETECTIONS,
  QUERY_DETECTIONS_PAGINATED,
} from '../../../../queries';
import { useDetectionContext } from '@investigator/features/detections/context';

const { excludeEntity } = i18n.pages.entityDetails;

const { success, error: errorText } = excludeEntity;

const {
  sent,
  closed,
  closeAndSend,
  cannotBeReverted,
  sendToAndClose,
  sendDetectionToServiceNow,
  escalateDetectionSuccess,
  escalateDetectionError,
} = i18n.pages.detections;

interface CloseDetectionContentProps {
  // TODO: Fix any type usage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchQueries?: any;
  onClose: () => void;
  refetchMain?: boolean;
  excludedEntitiesLoading?: boolean;
  serviceNowConfiguration?: ServiceNowEscalationConfiguration;
  setIsSentTo?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setPopoverType?: React.Dispatch<React.SetStateAction<string | null>>;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  isLoading?: boolean;
}

export const SendToContent = ({
  onClose,
  setIsSentTo,
  setIsLoading,
  setPopoverType,
  refetchQueries,
  excludedEntitiesLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
  isLoading,
  refetchMain,
}: CloseDetectionContentProps) => {
  const toast = useToast();
  const { detectionData } = useDetectionContext();
  const [_, setSearchParams] = useSearchParams();

  const refetchQueriesLocal = [
    {
      query: GET_DETECTIONS,
      variables: getDetectionVariables,
    },
  ];

  const { userRole } = useContext(AuthContext);
  const escalationConfigurationQuery =
    userRole === 'admin'
      ? useQueryEscalationConfigurations
      : useQueryEscalationConfigurationsPublic;

  const {
    data: serviceNowConfiguration,
    loading,
    error,
  } = escalationConfigurationQuery({});

  const escalationConfigurationId = useMemo(() => {
    return (
      serviceNowConfiguration?.queryEscalationConfigurations
        ?.escalation_configurations?.[0]?.escalation_configuration_id ?? ''
    );
  }, [serviceNowConfiguration]);

  const [
    escalateDetections,
    { data: _escalateDetectionsData, error: _escalateDetectionsError },
  ] = useEscalateDetections({
    refetchQueries: refetchQueries
      ? refetchQueries
      : !queryDetectionsPaginatedVariables
        ? refetchQueriesLocal
        : [
            ...refetchQueriesLocal,
            {
              query: QUERY_DETECTIONS_PAGINATED,
              variables: queryDetectionsPaginatedVariables,
            },
          ],
    onCompleted: () => {
      setIsLoading && setIsLoading(false);
      toast({
        title: success,
        description: escalateDetectionSuccess,
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });
      setPopoverType && setPopoverType(null);
      onClose();
      if (refetchMain) {
        setSearchParams((params) => {
          params.set('refetch_main', 'true');
          return params;
        });
      }
    },
    onError: () => {
      setIsLoading && setIsLoading(false);
      toast({
        title: errorText,
        description: escalateDetectionError,
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    },
  });

  const handleSendTo = () => {
    if (detectionData?.detection_id && detectionData?.tenant_info?.tenant_id) {
      escalateDetections({
        variables: {
          escalate_detections_info: [
            {
              escalation_configuration_id: escalationConfigurationId,
              detection_id: detectionData?.detection_id,
              tenant: detectionData?.tenant_info?.tenant_id,
            },
          ],
        },
      });
    }
  };

  const isSentTo = useMemo(() => {
    return !!detectionData?.escalation_data?.escalation_status;
  }, [detectionData]);

  useEffect(() => {
    setIsSentTo && setIsSentTo(isSentTo);
  }, [isSentTo, setIsSentTo]);

  return (
    <PopoverContent w="320px" data-testid="send-to-popover-content">
      {loading ? (
        <GraphQLReqStatus error={error} loading={loading}></GraphQLReqStatus>
      ) : (
        <>
          <PopoverCloseButton top={1}>
            <CloseSharpIcon size="sm" />
          </PopoverCloseButton>
          <PopoverHeader>
            <Heading textStyle="body-md-bold">
              {sendDetectionToServiceNow}
            </Heading>
          </PopoverHeader>
          <PopoverBody>
            <Text mb={2} textStyle="body-md">
              {i18n.formatString(sendToAndClose, {
                closed: <Text as="strong">{closed}</Text>,
                sent: <Text as="strong">{sent}</Text>,
              })}
            </Text>
            <Alert variant="subtle" status="warning">
              {cannotBeReverted}
            </Alert>
          </PopoverBody>
          <PopoverFooter alignItems="end">
            <Flex justifyContent="end">
              <Button
                variant="solid"
                colorScheme="blue"
                data-testid="send-to-button"
                onClick={() => {
                  handleSendTo();
                }}
                isDisabled={isSentTo} // || !escalation_configuration_id}
                isLoading={excludedEntitiesLoading || isLoading}
              >
                {closeAndSend}
              </Button>
            </Flex>
          </PopoverFooter>
        </>
      )}
    </PopoverContent>
  );
};
